// ═══════════════════════════════════════════════════════════════════════════
//  StrategyCardList — the deployed-bot cards in DEPLOYED STRATEGIES.
//
//  The heaviest section of LiveTrading.tsx (233 lines), lifted out in phase 3
//  (God 2026-08-26). The JSX is byte-identical to what it replaced; only the
//  values it used to close over now arrive as props.
//
//  HOW THE PROP LIST WAS BUILT. Not by regex — that reported 44 candidates, most
//  of them object keys and loop variables, and extracting on a guessed list would
//  be a gamble dressed as a refactor. The block was first hosted here with NO
//  props at all and the COMPILER was asked to enumerate its free identifiers.
//  It named exactly 38: 9 lucide icons, 5 foundation exports, and the 24 props
//  below. Every type here is copied from the original useState/useCallback
//  declaration rather than inferred, so nothing widens silently.
//
//  Two JSX expressions live here, not one — the empty-state check and the card
//  list — which is why the block is hosted whole inside a fragment rather than
//  unwrapped. Unwrapping on the assumption of a single IIFE is what broke the
//  first attempt.
//
//  Rendered by both lanes: DUMABATI and KALI are the same page component.
// ═══════════════════════════════════════════════════════════════════════════
import React from 'react';
import { BarChart2, Crosshair, Edit, Eye, Play, Square, Trash2, Webhook, Zap } from 'lucide-react';
import { AI_MODELS, API_BASE_URL, Deployment, FleetClient, fmtM, pCol, pSgn } from './foundation';
import { ExecControl } from '../components/ui/ExecControl';

export interface StrategyCardListProps {
  deployments: Deployment[];
  botStats: Record<string, { totalTrades: number; wins: number; losses: number; netPnl: number; winRate: number }>;
  /** Same shape, windowed to today-IST — the card leads with this so it and the
   *  header are measuring the same day (God 2026-09-21). Optional: an older
   *  caller that does not pass it simply shows the lifetime figures as before. */
  botStatsToday?: Record<string, { totalTrades: number; wins: number; losses: number; netPnl: number; winRate: number }>;
  botStatsLoaded?: boolean;          // true once /api/trades/bot-stats has answered at least once (God 2026-10-31)
  botDepth: Record<string, { bias: string; buyPct: number; option_symbol: string; security_id: number }>;
  fleetClients: FleetClient[];
  /** Which filter tab is active. 'positions' renders a different panel entirely,
   *  so this section checks it rather than assuming it is never that value. */
  showOnlySource: 'all' | 'mamta_ai' | 'manual' | 'webhook' | 'positions' | 'traffic' | 'promotion';
  isMobile: boolean;
  onOpenChart?: (symbol: string, model?: string) => void;

  getLotSize: (symbol: string) => number;
  getProductLabel: (p: string) => string;
  hydrateEditAssets: (botId: string) => void;
  openBotDepth: (d: Deployment) => void;
  testWebhook: (botId: string) => Promise<void> | void;
  toggleDep: (id: string) => void;
  fireDirectOrder: (...args: any[]) => Promise<any>;

  setBotMenuId: React.Dispatch<React.SetStateAction<string | null>>;
  setConfirmDelete: React.Dispatch<React.SetStateAction<string | null>>;
  setEditingBotId: React.Dispatch<React.SetStateAction<string | null>>;
  setSelectedBotForWebhook: React.Dispatch<React.SetStateAction<string | null>>;
  setNameAuto: React.Dispatch<React.SetStateAction<boolean>>;
  setNewDep: React.Dispatch<React.SetStateAction<Partial<Deployment>>>;
  setPricerSymbol: React.Dispatch<React.SetStateAction<string>>;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowOptionPricer: React.Dispatch<React.SetStateAction<boolean>>;
  setShowWebhookModal: React.Dispatch<React.SetStateAction<boolean>>;
}

export const StrategyCardList: React.FC<StrategyCardListProps> = ({
  deployments, botStats, botStatsToday, botStatsLoaded, botDepth, fleetClients, showOnlySource, isMobile, onOpenChart,
  getLotSize, getProductLabel, hydrateEditAssets, openBotDepth, testWebhook, toggleDep,
  fireDirectOrder, setBotMenuId, setConfirmDelete, setEditingBotId,
  setSelectedBotForWebhook, setNameAuto, setNewDep, setPricerSymbol,
  setShowModal, setShowOptionPricer, setShowWebhookModal,
}) => (
  <>
            {showOnlySource !== 'positions' && showOnlySource !== 'traffic' && showOnlySource !== 'promotion' && (() => {
              const filtered = deployments.filter(d => {
                if (showOnlySource === 'all') return true;
                if (showOnlySource === 'mamta_ai') return d.id.startsWith('mai_');
                if (showOnlySource === 'webhook') return d.model === 'WEBHOOK';
                if (showOnlySource === 'manual') return !d.id.startsWith('mai_') && d.model !== 'WEBHOOK';
                return true;
              });
              return filtered;
            })().length === 0 && <div style={{textAlign:'center',padding:40,color:'#475569',fontSize:12}}>No bots in this category.</div>}
            {showOnlySource !== 'positions' && showOnlySource !== 'traffic' && showOnlySource !== 'promotion' && (() => {
              const _flt = deployments.filter(d => {
                if (showOnlySource === 'all') return true;
                if (showOnlySource === 'mamta_ai') return d.id.startsWith('mai_');
                if (showOnlySource === 'webhook') return d.model === 'WEBHOOK';
                if (showOnlySource === 'manual') return !d.id.startsWith('mai_') && d.model !== 'WEBHOOK';
                return true;
              });
              // Group SuperChart (Mamta engine) bots first, then TradingView (webhook) bots
              const _sc = _flt.filter(d => d.id.startsWith('mai_'));
              const _tv = _flt.filter(d => !d.id.startsWith('mai_'));
              const _sorted = [..._sc, ..._tv];
              return _sorted.map((d, _i) => {
              const model = AI_MODELS.find(m => m.id === d.model);
              const isMamtaBot = d.id.startsWith('mai_');     // 🆕 v7.0
              const clientNames = d.targetClients?.includes('ALL') ? 'All Active Clients' :
                (d.targetClients || []).map(cid => fleetClients.find(c => c.id === cid)?.name?.split(' ')[0] || cid).join(', ');
              const _showHdr = _i === 0 || (_sorted[_i - 1].id.startsWith('mai_') !== isMamtaBot);
              return (
                <React.Fragment key={d.id}>
                {_showHdr && (
                  <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',margin: _i===0?'2px 2px 6px':'10px 2px 6px',padding:'5px 12px',borderRadius:9,background:isMamtaBot?'rgba(34,211,238,.08)':'rgba(251,191,36,.08)',border:`1px solid ${isMamtaBot?'rgba(34,211,238,.35)':'rgba(251,191,36,.35)'}`}}>
                    <span style={{fontSize:12,fontWeight:900,color:isMamtaBot?'#22d3ee':'#fbbf24',letterSpacing:.5}}>{isMamtaBot ? '📊 SUPERCHART CONNECTED' : '📡 TRADINGVIEW CONNECTED'}</span>
                    <span style={{fontSize:9.5,color:'#64748b'}}>· {(isMamtaBot?_sc:_tv).length} bot{(isMamtaBot?_sc:_tv).length===1?'':'s'} · signal {isMamtaBot?'Mamta / SuperChart engine':'TradingView webhook'} se aata hai</span>
                  </div>
                )}
                <div className="bot-card-flex" style={{background:isMamtaBot?'linear-gradient(135deg,#1a0d24,#0f0a1c)':'linear-gradient(135deg,#1e293b,#0f172a)',padding:'8px 10px',marginBottom:6,borderRadius:8,border:`1px solid ${d.status === 'RUNNING' ? '#10b98133' : (isMamtaBot?'#a855f733':'#1e293b')}`,display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,fontWeight:800,marginBottom:4,display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                      {model?.icon} {d.name}
                      {/* 🆕 v7.0 — Source Badge */}
                      {isMamtaBot && <span className="lt-hide-m" style={{fontSize:8,background:'rgba(168,85,247,.2)',color:'#a855f7',padding:'1px 5px',borderRadius:3,fontWeight:800,letterSpacing:1,border:'1px solid rgba(168,85,247,.4)'}}>🔱 MAMTA AI</span>}
                      {/* 🆕 SIGNAL SOURCE — SuperChart (system engine) vs TradingView (webhook) */}
                      {isMamtaBot
                        ? <span title="Signal SuperChart / Mamta engine se aata hai" style={{fontSize:8,background:'rgba(34,211,238,.15)',color:'#22d3ee',padding:'1px 6px',borderRadius:3,fontWeight:800,letterSpacing:.5,border:'1px solid rgba(34,211,238,.4)'}}>📊 SUPERCHART</span>
                        : <span title="Signal TradingView webhook se aata hai" style={{fontSize:8,background:'rgba(251,191,36,.15)',color:'#fbbf24',padding:'1px 6px',borderRadius:3,fontWeight:800,letterSpacing:.5,border:'1px solid rgba(251,191,36,.4)'}}>📡 TRADINGVIEW</span>}
                      {d.webhookEnabled && <Webhook size={12} color="#a855f7"/>}
                      {d.autoStrikeEnabled && <Zap size={12} color="#fbbf24"/>}
                      {d.autoFireEnabled && <span title="Auto-Fire ON" style={{fontSize:8,background:'rgba(16,185,129,.15)',color:'#10b981',padding:'1px 5px',borderRadius:3,fontWeight:800}}>🔥 AUTO</span>}
                      {/* PENDING APPROVAL (God 2026-08-28). On KALI a bot that is not
                          yet cleared for live is LISTED, but it must never borrow the
                          PAPER badge to do it — a paper badge on the real-money page is
                          the exact 2026-08-26 defect. It says what it is instead. */}
                      {(d as any).pendingApproval
                        ? <span title="Listed on KALI, but not cleared to trade live yet. Execution stays blocked until God approves the Pine V6 code."
                                style={{fontSize:8,background:'rgba(251,191,36,.15)',color:'#fbbf24',padding:'1px 6px',borderRadius:3,fontWeight:800,letterSpacing:.4,border:'1px solid rgba(251,191,36,.45)'}}>
                            ⏳ {(d as any).pendingLabel || 'PENDING'}
                          </span>
                        : <span style={{fontSize:8,background:d.tradingMode==='PAPER'?'rgba(56,189,248,.15)':'rgba(239,68,68,.15)',color:d.tradingMode==='PAPER'?'#38bdf8':'#ef4444',padding:'1px 5px',borderRadius:3,fontWeight:800}}>{d.tradingMode}</span>}
                    </div>
                    <div style={{fontSize:9,color:'#64748b',marginBottom:4,display:'flex',gap:8,flexWrap:'wrap'}}>
                      <span>{d.model} · {getProductLabel(d.product)}</span>
                      <span style={{color:'#818cf8'}}>👥 {clientNames}</span>
                      <span style={{color:'#4ade80'}}>🏦 {(d.targetBrokers || []).length} brokers</span>
                    </div>
                    {(() => {
                      // NO PAPER MONEY ON THE REAL-MONEY PAGE (God 2026-08-26,
                      // zero tolerance; re-checked in the browser 2026-08-28).
                      // botStats is keyed by bot id and is NOT lane-scoped — it
                      // did not need to be while KALI drew no cards at all. The
                      // moment KALI started listing the fleet, these three cells
                      // began showing each bot's PAPER P&L, trade count and W:L
                      // on the page that spends real money. The badge was honest
                      // and the numbers were not, which is the same lie in a
                      // quieter place.
                      //
                      // A pending bot has no live record yet, so the truthful
                      // value is "not started" — not a zero, which would read as
                      // "traded and broke even", and certainly not the paper run.
                      if ((d as any).pendingApproval) {
                        return (
                          <div className="bot-stats-row" title="Live stats begin after Pine V6 approval. Paper results live on DUMABATI.">
                            <div><label>PNL</label><b style={{color:'#64748b'}}>—</b></div>
                            <div><label>Trades</label><b style={{color:'#64748b'}}>—</b></div>
                            <div><label>W : L</label><b style={{color:'#64748b'}}>—</b></div>
                          </div>
                        );
                      }
                      // TWO WINDOWS, BOTH NAMED (God 2026-09-21). The card used
                      // to print one unlabelled set of numbers covering the whole
                      // book while the header's headline was today's — so a
                      // +₹7.7K header sat above a −₹74.0K card and read as a
                      // contradiction when both were right. Today leads, because
                      // that is what the header leads with; the lifetime figure
                      // is kept, smaller, directly beneath it.
                      const bs = botStats[d.id];
                      const td = botStatsToday?.[d.id];
                      // SERVER IS THE AUTHORITY (God 2026-10-31 clean slate): once bot-stats has answered, a bot with no
                      // rows shows 0 - never this device's cached counters (the cards kept 433 / 447 old trades after the
                      // ledger was emptied). Before the first answer the cache is still shown so the list is not blank.
                      const pnl = bs ? bs.netPnl : (botStatsLoaded ? 0 : d.pnl);
                      const tr  = bs ? bs.totalTrades : (botStatsLoaded ? 0 : d.trades);
                      const w   = bs ? bs.wins : (botStatsLoaded ? 0 : d.wins);
                      const l   = bs ? bs.losses : (botStatsLoaded ? 0 : d.losses);
                      // Absent today-stats = the fetch has not landed yet, which
                      // is not the same as "traded nothing today". Show the
                      // lifetime line alone rather than a zero that lies.
                      const sub = (v: React.ReactNode) => (
                        <span style={{fontSize:8,fontWeight:700,color:'#64748b',marginLeft:4}}>{v}</span>
                      );
                      return (
                        <div className="bot-stats-row"
                             title={td ? 'Big number = TODAY (same window as the header). Small = since the stats epoch.'
                                       : 'Since the stats epoch — today’s split has not loaded yet.'}>
                          <div>
                            <label>PNL {td ? 'today' : ''}</label>
                            <b style={{color:pCol(td ? td.netPnl : pnl)}}>
                              {pSgn(td ? td.netPnl : pnl)}₹{fmtM(td ? td.netPnl : pnl)}
                            </b>
                            {td && sub(<>all {pSgn(pnl)}₹{fmtM(pnl)}</>)}
                          </div>
                          <div>
                            <label>Trades {td ? 'today' : ''}</label>
                            <b>{td ? td.totalTrades : tr}</b>
                            {td && sub(<>all {tr}</>)}
                          </div>
                          <div>
                            <label>W : L {td ? 'today' : ''}</label>
                            <b>
                              <span style={{color:'#10b981'}}>{td ? td.wins : w}</span>
                              <span style={{color:'#64748b'}}> : </span>
                              <span style={{color:'#ef4444'}}>{td ? td.losses : l}</span>
                            </b>
                            {td && sub(<>all {w}:{l}</>)}
                          </div>
                        </div>
                      );
                    })()}
                    {(() => {
                      // 📊 LIVE DEPTH — order-book BUY/SELL pressure for this bot's open option
                      const bd = botDepth[d.id];
                      if (!bd) return null;
                      const isBuy = bd.bias.includes('BUY'), isSell = bd.bias.includes('SELL');
                      const col = isBuy ? '#34d399' : isSell ? '#f87171' : '#94a3b8';
                      const bg  = isBuy ? 'rgba(16,185,129,.12)' : isSell ? 'rgba(239,68,68,.12)' : 'rgba(148,163,184,.1)';
                      return (
                        <div style={{marginTop:6,display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                          <span title={`Live order-book on ${bd.option_symbol}`}
                            style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:9,fontWeight:900,
                              padding:'2px 8px',borderRadius:5,background:bg,border:`1px solid ${col}`,color:col}}>
                            📊 {bd.bias.replace('_',' ')} · {bd.buyPct}% buy
                          </span>
                          {/* mini buy/sell bar */}
                          <span style={{flex:'0 0 60px',height:5,borderRadius:3,overflow:'hidden',background:'rgba(239,68,68,.45)',display:'inline-block'}}>
                            <span style={{display:'block',height:'100%',width:`${bd.buyPct}%`,background:'#10b981'}}/>
                          </span>
                          <span style={{fontSize:8,color:'#475569'}}>{bd.option_symbol}</span>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="bot-actions" style={{display:'flex',gap:5,alignItems:'flex-start',flexWrap:'wrap',justifyContent:'flex-end'}}>
                    {/* Direct SuperChart — DUAL view: the bot's INDEX chart + its (open/ATM)
                        OPTION chart side-by-side, with a smart bar to hop between every
                        symbol in the bot's basket (~1-2s per switch). */}
                    <button onClick={async ()=>{
                      const legs = (d.assets||[]).filter((x:any)=>x && (x.baseSymbol||x.symbol) && x.enabled!==false);
                      // Repair legacy baseSymbols — an old split(' ')[0] stored
                      // "BANK NIFTY" as just "BANK", which resolved to dead
                      // contracts like "BANK FUT" (empty pre-market panes).
                      const _dispSym = (s:string) => {
                        const u = s.replace(/_/g,' ').toUpperCase().trim();
                        // God 2026-07-16 dup-chip fix: NIFTY == NIFTY 50 (same index) —
                        // "NIFTY" aur "NIFTY_50" ko EK canonical me collapse karo, warna
                        // chip-bar me NIFTY 2 baar aata tha + multi-TF (5s ek chip, 1s
                        // dusre chip) split ho jata tha. Ab dono TF ek hi chip par merge.
                        return u==='BANK'||u==='BANKNIFTY'||u==='BANK NIFTY' ? 'BANK NIFTY'
                          : u==='FIN'||u==='FINNIFTY'||u==='FIN NIFTY' ? 'FIN NIFTY'
                          : u==='NIFTY'||u==='NIFTY50'||u==='NIFTY 50' ? 'NIFTY 50'
                          : u==='MIDCP'||u==='MIDCPNIFTY' ? 'MIDCPNIFTY' : u;
                      };
                      // CAP the switcher basket — a quick-add-all bot can hold 100+
                      // symbols; passing them all made the chart prefetch 100 /atm
                      // calls and nginx's per-IP rate limit then starved EVERY other
                      // endpoint (ceo-feed/feed-status 503 storm).
                      const syms = Array.from(new Set(
                        (legs.length?legs:(d.assets||[])).map((x:any)=>_dispSym(String(x.baseSymbol||x.symbol)))
                      )).slice(0, 12);
                      if (!syms.length) {
                        // SHOOL/STHAMBH nav fix (God 2026-07-25): a deployment with
                        // missing/legacy assets fell back to 'NIFTY 50' — the chart
                        // opened WITHOUT the bot's symbols. Pull the bot's REAL
                        // instruments from its tagged pine versions (the same store
                        // the engine's per-leg router reads) before defaulting.
                        try {
                          const short = String(d.id||'').startsWith('mai_') ? String(d.id).slice(4) : String(d.id);
                          const r = await fetch(`${API_BASE_URL}/api/bot/versions/${encodeURIComponent(short)}`);
                          const j = r.ok ? await r.json() : null;
                          const tagged = Array.from(new Set(
                            (Array.isArray(j?.versions) ? j.versions : [])
                              .filter((v:any)=>v?.status!=='ARCHIVED' && Array.isArray(v?.symbols))
                              .flatMap((v:any)=>v.symbols as string[])
                          )).slice(0, 12).map(s=>_dispSym(String(s)));
                          syms.push(...Array.from(new Set(tagged)));
                        } catch { /* fall through to default */ }
                      }
                      if (!syms.length) syms.push('NIFTY 50');
                      // If the bot HOLDS an option right now, open that exact contract on pane 2.
                      const openOpt = botDepth[d.id]?.option_symbol || '';
                      // ⏱ PER-SYMBOL TF map — chart symbol-switch par har symbol
                      // apne configured TF par khulta hai (asset.timeframe).
                      const tfMap: Record<string, string> = {};
                      const tfListMap: Record<string, string[]> = {};   // per-index distinct TFs (God multi-TF: NIFTY 1s + 5s dono)
                      (legs.length?legs:(d.assets||[])).forEach((x:any)=>{
                        if (x.timeframe) {
                          const dsym = _dispSym(String(x.baseSymbol||x.symbol));
                          tfMap[dsym] = String(x.timeframe);
                          const lst = (tfListMap[dsym] = tfListMap[dsym] || []);
                          if (!lst.includes(String(x.timeframe))) lst.push(String(x.timeframe));
                        }
                      });
                      // SEGMENT-AWARE chart: VEG opens CE|INDEX|PE, SHOOL opens
                      // FUTURES-only ladder, STHAMBH opens CASH 15m|4H|1D.
                      // BOT IDENTITY FIRST (segment rules sacred — Shool=FUTURES,
                      // Sthambh=CASH chahe asset .type kuch bhi ho); asset ka
                      // .type/.segment sirf custom bots ke liye fallback hai.
                      // (Pehle model==='SHOOL' exact match tha — server bot ka
                      // model 'CUSTOM' aate hi Shool OPTION view me khul jaata tha.)
                      const _idm = `${String(d.id || '')} ${String(d.model || '')} ${String(d.name || '')}`.toUpperCase();
                      const _a0 = (legs[0] || d.assets?.[0] || {}) as any;
                      const _at = `${String(_a0.type || '')} ${String(_a0.segment || '')}`.toUpperCase();
                      const segment = /SHOOL/.test(_idm) || /FUT/.test(_at) ? 'FUTURE'
                        : /STHAMBH/.test(_idm) || /STOCK|CASH|EQUITY|ETF/.test(_at) ? 'CASH'
                        : 'OPTION';
                      try {
                        localStorage.setItem('jc_chart_bot_ctx', JSON.stringify({
                          botId: d.id, botName: d.name, symbols: syms, segment,
                          optionSymbol: openOpt || null, ts: Date.now(),
                          // ONE FLOW: the chart auto-applies the BOT's saved Pine code at
                          // the BOT's own timeframe (Pine Editor saves under veg/shool/…).
                          tf: (d as any).timeframe || '',
                          tfMap, tfListMap,
                          pineBot: d.id.startsWith('mai_') ? d.id.slice(4) : d.id,
                        }));
                      } catch {}
                      onOpenChart?.(syms[0], d.model);
                    }} className="lt-btn" style={{padding:'6px 9px',background:'linear-gradient(135deg,#2563eb,#1d4ed8)',color:'#fff',fontSize:8}} title="Open this bot's chart — VEG: CE|INDEX|PE · SHOOL: FUT+SPOT · STHAMBH: CASH 15m+1D (smart symbol switch)">
                      <BarChart2 size={11}/> CHART
                    </button>
                    {!isMobile && d.webhookEnabled && (
                      <button onClick={()=>{setSelectedBotForWebhook(d.id); setShowWebhookModal(true);}} className="lt-btn" style={{padding:'6px 9px',background:'linear-gradient(135deg,#7c3aed,#a855f7)',color:'#fff',fontSize:8}}>
                        <Eye size={11}/> WEBHOOK
                      </button>
                    )}
                    {/* 🆕 v6.0 — Per-bot Pricer button */}
                    {!isMobile && <button onClick={()=>{
                      const sym = d.assets[0]?.baseSymbol || 'NIFTY';
                      setPricerSymbol(sym);
                      setShowOptionPricer(true);
                    }} className="lt-btn" style={{padding:'6px 9px',background:'linear-gradient(135deg,#7c3aed,#5b21b6)',color:'#fff',fontSize:8}} title="Open Smart Option Pricer">
                      <Crosshair size={11}/> PRICER
                    </button>}
                    {/* M-H2 (God D25): on a LIVE bot TEST is a REAL BUY through Dhan, so it fires
                        only on a 1.4 s hold. PAPER TEST is a webhook test and stays a tap. */}
                    {!isMobile && (d.tradingMode === 'LIVE'
                      ? <ExecControl compact live tone="info" title="LIVE: real Dhan BUY — hold 1.4 s"
                          onConfirm={()=>{
                            const sym = d.assets[0]?.baseSymbol || 'NIFTY';
                            const optType = d.assets[0]?.optType || 'CE';
                            const qty = d.assets[0]?.qty || getLotSize(sym);
                            fireDirectOrder(sym, 'BUY', qty, 'LIVE', optType, 'ATM', d.product, d.id);
                          }}>
                          🧪 HOLD · LIVE TEST
                        </ExecControl>
                      : <button onClick={()=>testWebhook(d.id)} className="lt-btn" style={{padding:'6px 9px',background:'linear-gradient(135deg,#0891b2,#0e7490)',color:'#fff',fontSize:8}} title="PAPER: webhook test">
                          🧪 TEST
                        </button>)}
                    {!isMobile && <button onClick={()=>openBotDepth(d)}
                      className="lt-btn" style={{padding:'6px 9px',background:'linear-gradient(135deg,#0d9488,#0f766e)',color:'#fff',fontSize:8}} title="Open 20-level order-book depth for this bot's symbol">
                      <BarChart2 size={11}/> DEPTH
                    </button>}
                    <button onClick={()=>{setEditingBotId(d.id); setNewDep(d); setNameAuto(false); setShowModal(true); hydrateEditAssets(d.id);}} className="lt-btn" style={{padding:'6px 9px',background:'linear-gradient(135deg,#eab308,#ca8a04)',color:'#fff',fontSize:8}}>
                      <Edit size={11}/> EDIT
                    </button>
                    {/* God 2026-08-28: "bas unki live execution ruki rahegi jab tak
                        approval na mile". The card is complete and every other control
                        works; this ONE control is what approval gates. Disabled rather
                        than hidden, so the operator can see the gate exists. */}
                    {/* M-H2 (God D25): stopping or starting a LIVE bot is a money action — hold 1.4 s. */}
                    {d.tradingMode === 'LIVE' && !(d as any).pendingApproval && (d as any).armSync !== 'PENDING' ? (
                      <ExecControl compact live tone={d.status === 'RUNNING' ? 'neg' : 'pos'}
                        title={d.status === 'RUNNING' ? 'Hold 1.4 s to STOP this LIVE bot' : 'Hold 1.4 s to START this LIVE bot'}
                        onConfirm={()=>toggleDep(d.id)}>
                        {d.status === 'RUNNING' ? <Square size={11}/> : <Play size={11}/>} HOLD · {d.status === 'RUNNING' ? 'STOP' : 'START'}
                      </ExecControl>
                    ) : (
                    <button onClick={()=>{ if ((d as any).pendingApproval) return; toggleDep(d.id); }}
                      disabled={!!(d as any).pendingApproval}
                      title={(d as any).pendingApproval ? 'Blocked until Pine V6 is approved for this bot' : undefined}
                      className="lt-btn" style={{padding:'6px 9px',background:(d as any).pendingApproval ? 'linear-gradient(135deg,#334155,#1e293b)' : (d.status === 'RUNNING' ? 'linear-gradient(135deg,#dc2626,#b91c1c)' : 'linear-gradient(135deg,#059669,#047857)'),color:(d as any).pendingApproval ? '#94a3b8' : '#fff',fontSize:8,cursor:(d as any).pendingApproval ? 'not-allowed' : 'pointer',opacity:(d as any).pendingApproval ? .75 : 1}}>
                      {(d as any).pendingApproval
                        ? <>⏳ PENDING</>
                        : (d as any).armSync === 'PENDING'
                          ? <>{d.status === 'RUNNING' ? 'ARMING…' : 'STOPPING…'}</>
                          : <>{d.status === 'RUNNING' ? <Square size={11}/> : <Play size={11}/>} {d.status === 'RUNNING' ? 'STOP' : 'START'}</>}
                    </button>
                    )}
                    <button onClick={()=>setConfirmDelete(d.id)} className={`lt-btn ${isMobile ? 'bot-del' : ''}`} style={{padding:'6px 9px',background:'linear-gradient(135deg,#991b1b,#7f1d1d)',color:'#fff',fontSize:8}} title="Delete Bot Permanently">
                      <Trash2 size={11}/>
                    </button>
                    {isMobile && (
                      <button onClick={()=>setBotMenuId(d.id)} className="lt-btn" title="More actions"
                        style={{padding:'6px 12px',background:'var(--surface-2,#1e293b)',color:'var(--text,#e2e8f0)',
                          border:'1px solid var(--border,#1e293b)',fontSize:14,fontWeight:900}}>
                        •••
                      </button>
                    )}
                  </div>
                </div>
                </React.Fragment>
              );
              });
            })()}
  </>
);

export default StrategyCardList;
